import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { certifications } from '../data';
import profilePhoto from '../assets/pradap-photo.jpg';
import { useTheme } from '../contexts/ThemeContext';

// Animated Counter Component
const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string }> = ({
  end,
  duration = 2,
  suffix = ''
}) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    if (inView) {
      let startTime: number;
      let animationFrame: number;

      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(easeOutQuart * end);

        setCount(currentCount);

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => cancelAnimationFrame(animationFrame);
    }
  }, [inView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const About: React.FC = () => {
  const { theme, themeClasses, isLight } = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const headingClass = isLight ? 'text-zinc-900' : 'text-white';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="about" className={`pt-8 pb-16 sm:pt-12 sm:pb-20 ${themeClasses.bg}`}>
      <div className="container mx-auto px-2 sm:px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className={`text-4xl font-bold mb-4 ${themeClasses.text}`}>About Me</h2>
          <p className={`text-xl ${themeClasses.textMuted}`}>Get to know me better</p>
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={itemVariants} className="space-y-6">
            <div className="text-center lg:text-left">
              <div className="profile-frame w-48 h-48 mx-auto lg:mx-0 mb-6 relative">
                <img 
                  src={profilePhoto} 
                  alt="Pradap Pandiyan" 
                  className="w-full h-full object-contain"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  theme === 'slate' ? 'from-teal-400/20' :
                  theme === 'midnight' ? 'from-sky-400/20' :
                  theme === 'studio' ? 'from-teal-700/20' :'from-sky-400/20'
                } to-transparent`}></div>
                <div className={`absolute top-3 right-3 w-4 h-4 rounded-full animate-terminal-blink ${
                  theme === 'slate' ? 'bg-teal-400' :
                  theme === 'midnight' ? 'bg-sky-400' :
                  theme === 'studio' ? 'bg-teal-700' :'bg-sky-400'
                }`}></div>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${headingClass}`}>
                Pradap Pandiyan
              </h3>
              <h4 className={`text-xl font-semibold mb-4 ${
                theme === 'slate' ? 'text-teal-400' :
                theme === 'midnight' ? 'text-sky-400' :
                theme === 'studio' ? 'text-teal-700' :'text-teal-400'
              }`}>
                Platform Automation Lead & Software Developer
              </h4>
              <p className={`${themeClasses.textMuted} leading-relaxed mb-4`}>
                I'm a passionate and results-driven Platform Automation Lead and Software Developer with extensive 
                experience in testing automation, mobile app development, and quality assurance. With a strong 
                background in both technical and leadership roles, I bring a comprehensive approach to software 
                quality and development.
              </p>
              <p className={`${themeClasses.textMuted} leading-relaxed`}>
                My journey includes valuable experience at leading companies like M2 and Exinity, where I've 
                contributed to building robust testing frameworks and ensuring high-quality software delivery. 
                I'm also proud to have developed Auto Maintenance, my first iOS app focused on goal tracking and bucket list management,
                and published the Super Pancake Automation NPM package for the developer community.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <motion.div
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                  rotateX: -5,
                  transition: { duration: 0.3 }
                }}
                className={`${themeClasses.card} p-6 text-center rounded-xl transform-gpu perspective-1000`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className={`text-3xl font-bold mb-2 bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}>
                  <AnimatedCounter end={14} suffix="+" />
                </div>
                <div className={themeClasses.textMuted}>Years Experience</div>
              </motion.div>
              <motion.div
                whileHover={{
                  scale: 1.05,
                  rotateY: -5,
                  rotateX: -5,
                  transition: { duration: 0.3 }
                }}
                className={`${themeClasses.card} p-6 text-center rounded-xl transform-gpu perspective-1000`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className={`text-3xl font-bold mb-2 bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}>
                  <AnimatedCounter end={50} suffix="+" />
                </div>
                <div className={themeClasses.textMuted}>Projects Completed</div>
              </motion.div>
              <motion.div
                whileHover={{
                  scale: 1.05,
                  rotateY: 5,
                  rotateX: 5,
                  transition: { duration: 0.3 }
                }}
                className={`${themeClasses.card} p-6 text-center rounded-xl transform-gpu perspective-1000`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className={`text-3xl font-bold mb-2 bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}>
                  <AnimatedCounter end={2} />
                </div>
                <div className={themeClasses.textMuted}>Certifications</div>
              </motion.div>
              <motion.div
                whileHover={{
                  scale: 1.05,
                  rotateY: -5,
                  rotateX: 5,
                  transition: { duration: 0.3 }
                }}
                className={`${themeClasses.card} p-6 text-center rounded-xl transform-gpu perspective-1000`}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className={`text-3xl font-bold mb-2 bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}>
                  <AnimatedCounter end={100} suffix="%" />
                </div>
                <div className={themeClasses.textMuted}>Client Satisfaction</div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-6">
            <div>
              <h3 className={`text-2xl font-bold mb-6 ${headingClass}`}>
                Professional Certifications
              </h3>
              <div className="space-y-4">
                {certifications.map((cert) => (
                  <motion.div
                    key={cert.name}
                    whileHover={{ scale: 1.02 }}
                    className={`${themeClasses.card} p-6 rounded-xl`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className={`font-semibold ${headingClass}`}>
                        {cert.name}
                      </h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        theme === 'slate' ? 'bg-teal-400/20 text-teal-400' :
                        theme === 'midnight' ? 'bg-sky-400/20 text-sky-400' :
                        theme === 'studio' ? 'bg-teal-700/20 text-teal-700' :'bg-sky-400/20 text-sky-400'
                      }`}>
                        {cert.date}
                      </span>
                    </div>
                    <p className={`${themeClasses.textMuted} mb-2`}>
                      {cert.issuer}
                    </p>
                    {cert.url && (
                      <a
                        href={cert.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`hover:underline text-sm ${
                          theme === 'slate' ? 'text-teal-400 hover:text-teal-400' :
                          theme === 'midnight' ? 'text-sky-400 hover:text-sky-400' :
                          theme === 'studio' ? 'text-teal-700 hover:text-teal-700' :
                          'text-teal-400 hover:text-sky-400'
                        }`}
                      >
                        View Certificate
                      </a>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <h3 className={`text-2xl font-bold mb-4 ${headingClass}`}>
                Automation Architecture & Management
              </h3>
              <div className="space-y-4">
                {[
                  {
                    title: 'Automation Framework Architecture',
                    description: 'Designing scalable, maintainable automation frameworks from ground up using modern technologies and best practices'
                  },
                  {
                    title: 'Cross-Platform Testing Strategy',
                    description: 'Implementing comprehensive testing strategies for web, mobile (iOS/Android), and API platforms'
                  },
                  {
                    title: 'DevOps & CI/CD Integration',
                    description: 'Architecting seamless integration of automation into DevOps pipelines for continuous quality delivery'
                  },
                  {
                    title: 'Team Leadership & Mentoring',
                    description: 'Building and leading high-performing QA teams while fostering automation culture and knowledge sharing'
                  },
                  {
                    title: 'Quality Engineering Strategy',
                    description: 'Developing comprehensive quality strategies that align with business objectives and technical requirements'
                  },
                  {
                    title: 'Performance & Scalability Optimization',
                    description: 'Optimizing automation frameworks for performance, scalability, and reliability across enterprise environments'
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-lg border ${themeClasses.card}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        theme === 'slate' ? 'bg-teal-400' :
                        theme === 'midnight' ? 'bg-sky-400' :
                        theme === 'studio' ? 'bg-teal-700' :'bg-sky-400'
                      }`} />
                      <div>
                        <h4 className={`${headingClass} font-semibold mb-1`}>{item.title}</h4>
                        <p className={`${themeClasses.textSoft} text-sm`}>{item.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
